import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { Switch, Button, Select } from "antd";
import moment from "moment";
import PropTypes from "prop-types";
import { ReloadOutlined, StopOutlined } from "@ant-design/icons";
import styles from "./VisualitzacioLiniaTemporalControls.module.css";
import {
  getDataSeleccionada,
  getDataIniciAsMoment,
  getDataFiAsMoment
} from "../../reducers";
import { omplirDataSeleccionadaVisualitzacio } from "../../actions";
import { FORMAT_YYYYMMDD } from "../../constantsObservacions";

const Option = Select.Option;
const defaultVelocitat = 1;
const defaultExecuteInterval = 1500; //1.5seg

class VisualitzacioLiniaTemporalControls extends PureComponent {
  animacio = null;
  state = {
    velocitat: defaultVelocitat,
    isAnimacioActiva: false,
    isBucleActiu: true
  };

  aturarAnimacio() {
    this.setState(state => ({
      ...state,
      isAnimacioActiva: false
    }));

    if (this.animacio) {
      clearInterval(this.animacio);
    }
  }

  /**
   * Retrocedeix un step la data seleccionada.
   */
  stepBackward() {
    //Agafem la data seleccionada i li restem 1 dia.
    const { dataSeleccionada, dataInici } = this.props;
    //Posible data segons step
    const novaData = moment(dataSeleccionada, FORMAT_YYYYMMDD).subtract(
      1,
      "days"
    );

    if (novaData.isBefore(dataInici)) {
      return;
    }
    this.props.omplirDataSeleccionadaVisualitzacio(novaData);
  }

  /**
   * Avança un step la data seleccionada. Si l'opció de bucle esta activa, al arribar al
   * final de l'última data disponible, torna a la data d'inici. En cas
   * de no estar el bucle activat, s'atura.
   */
  stepForward() {
    //Agafem la data seleccionada i li suma 1 dia.
    const { dataSeleccionada, dataFi, dataInici } = this.props;
    const { isBucleActiu } = this.state;
    const novaData = moment(dataSeleccionada, FORMAT_YYYYMMDD).add(1, "days");

    let data = novaData;
    if (novaData.isAfter(moment(dataFi).startOf("day"))) {
      if (isBucleActiu) {
        //Si el bucle esta activat tornem al inici del periode horari
        data = dataInici;
      } else {
        return;
      }
    }

    this.props.omplirDataSeleccionadaVisualitzacio(data);
  }

  handleClickFastBackward = () => {
    const iniciPeriode = this.props.dataInici;
    this.props.omplirDataSeleccionadaVisualitzacio(iniciPeriode);
  };

  handleClickFastForward = () => {
    const fiPeriode = this.props.dataFi;
    this.props.omplirDataSeleccionadaVisualitzacio(fiPeriode);
  };

  /**
   * La funció ha de restar 1 dia a la data seleccionada i llençar l'acció amb la nova data resultant.
   * En cas que la nova data resultant sigui anterior a la primera data disponible no es llençarà l'acció.
   */
  handleClickStepBackward = () => {
    this.stepBackward();
  };

  /**
   * La funció a de sumar 1 dia a la data seleccionada i llençar l'acció amb la nova data resultant.
   * En cas que la nova data resultant sigui posterior a l'ultima data disponible no es llençarà l'acció.
   */
  handleClickStepForward = () => {
    this.stepForward();
  };

  /**
   * Inici o atura la reproducció a la velocitat especificada al "select". Si el switch de bucle esta actiu al arribar al final es torna al inici.
   */
  handleClickPlayStop = () => {
    const callback = () => {
      //Aquesta funció s'executa despŕes de canviar l'estat. Per tant s'ha d'eliminar el setInterval quan no esta activa la animacio
      if (!this.state.isAnimacioActiva) {
        clearInterval(this.animacio);
        return;
      }

      this.animacio = setInterval(() => {
        //S'ha de fer el mateix que fa el handleClickStepForward
        this.stepForward();

        const isArribatUltimaDataDisponible = moment(
          this.props.dataSeleccionada
        )
          .add(1, "day")
          .isAfter(this.props.dataFi, "day");
        //S'atura l'animació quan bucle is false i hem arribat al final de les dades disponibles
        if (!this.state.isBucleActiu && isArribatUltimaDataDisponible) {
          this.aturarAnimacio();
        }
      }, defaultExecuteInterval / this.state.velocitat);
    };

    //Es modifica l'estat de l'animació i després s'executa la funcionalitat
    this.setState(
      state => ({
        ...state,
        isAnimacioActiva: !state.isAnimacioActiva
      }),
      callback
    );
  };

  handleChangeVelocitat = value => {
    this.setState(state => ({
      ...state,
      velocitat: Number(value)
    }));
  };

  handleSwitchBucle = value => {
    this.setState(state => ({
      ...state,
      isBucleActiu: value
    }));
  };

  componentWillUnmount() {
    if (this.animacio) {
      clearInterval(this.animacio);
    }
  }

  render() {
    const { isAnimacioActiva } = this.state;
    return (
      <div>
        <div className={styles.LiniaTemporalControls}>
          <div className={styles.LiniaTemporalControlsBottons}>
            <Button
              icon="fast-backward"
              onClick={this.handleClickFastBackward}
              size="large"
            />
            <Button
              icon="step-backward"
              onClick={this.handleClickStepBackward}
              size="large"
            />
            <Button
              icon={!isAnimacioActiva ? "caret-right" : "pause"}
              onClick={this.handleClickPlayStop}
              size="large"
            />
            <Button
              icon="step-forward"
              onClick={this.handleClickStepForward}
              size="large"
            />
            <Button
              icon="fast-forward"
              onClick={this.handleClickFastForward}
              size="large"
            />
          </div>
        </div>
        <div className={styles.LiniaTemporalControlsIntervalVelocitat}>
          <div className={styles.LiniaTemporalControlsBucle}>
            <span className={styles.etiqueta}>Bucle:</span>
            <Switch
              checkedChildren={<ReloadOutlined />}
              unCheckedChildren={<StopOutlined />}
              defaultChecked={this.state.isBucleActiu}
              onChange={this.handleSwitchBucle}
            />
          </div>
          <div className={styles.LiniaTemporalControlsVelocitat}>
            <span className={styles.etiqueta}>Velocitat:</span>
            <Select
              onChange={this.handleChangeVelocitat}
              defaultActiveFirstOption={false}
              defaultValue={"1x"}
              disabled={isAnimacioActiva}
              size={"small"}
            >
              <Option value="1">1x</Option>
              <Option value="2">2x</Option>
            </Select>
          </div>
        </div>
      </div>
    );
  }
}

VisualitzacioLiniaTemporalControls.propTypes = {
  dataSeleccionada: PropTypes.string.isRequired,
  dataInici: PropTypes.object.isRequired,
  dataFi: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  return {
    dataSeleccionada: getDataSeleccionada(state),
    dataInici: getDataIniciAsMoment(state),
    dataFi: getDataFiAsMoment(state)
  };
};

export default connect(mapStateToProps, {
  omplirDataSeleccionadaVisualitzacio
})(VisualitzacioLiniaTemporalControls);
